import { __, setLocaleData } from '@wordpress/i18n';
import Disclosure from 'a11y/Disclosure';
import createScreenReaderText from 'a11y/lib/createScreenReaderText';
import getElementFromComponent from 'utils/getElementFromComponent';
import breakpoints from 'utils/breakpoints';
import './header-search.scss';

/**
 * Component for the header search form.
 */
export default class HeaderSearch {
  /**
   * Start the component
   */
  constructor({ element, children, options }) {
    this.element = element;
    this.children = children;
    this.options = options;

    // Register text domain
    setLocaleData({ '': {} }, 'sciencenews');

    // Bind class methods.
    this.moveComponent = this.moveComponent.bind(this);
    this.updateControllerText = this.updateControllerText.bind(this);

    /**
     * The nav-bar wrapper element.
     * @type {HTMLElement}
     */
    this.navBarWrapper = getElementFromComponent('wrapper', 'nav-bar');

    /**
     * The nav-bar element (child of the nav-bar.wrapper)
     * @type {HTMLElement}
     */
    this.navBar = getElementFromComponent('nav-bar', 'nav-bar');

    /**
     * The nav-bar 'action list' element.
     * @type {HTMLElement}
     */
    this.actionsList = getElementFromComponent('actions', 'nav-bar');

    // Ensure the header-search is positioned correctly for the current screen size.
    this.moveComponent();
    breakpoints.lgMin.addListener(this.moveComponent);

    this.init();
    // Update the controller text to ensure it reflects the correct state.
    this.updateControllerText();
  }

  /**
   * Conditionally relocate the this component based on the matching breakpoint.
   */
  moveComponent() {
    const { controller } = this.children;

    if (
      this.navBarWrapper !== null
      && this.navBar !== null
      && this.actionsList !== null
    ) {
      const controllerParent = controller.parentElement;
      const targetParent = this.element.parentElement;

      if (breakpoints.lgMin.matches) {
        // For lgMin breakpoint, the header-search should be
        // a child of nav-bar before the actions list.
        if (controllerParent !== this.navBar && targetParent !== this.navBar) {
          // Move the header-search elements into the nav-bar, directly before the actions list.
          // Target (this.element) first – order matters!
          this.navBar.insertBefore(this.element, this.actionsList);
        }
      } else if (
        controllerParent !== this.navBarWrapper
        && targetParent !== this.navBarWrapper
      ) {
        // For breakpoints < lgMin, the header-search should
        // be the last child of the nav-bar.wrapper.
        // Move the header-search elements into the nav-bar wrapper as the last children.
        // Target (this.element) first – order matters!
        this.navBarWrapper.appendChild(this.element);
      }
    }
  }

  /**
   * Initialize the Disclosure class and add the screen reader description.
   */
  init() {
    const { controller, target } = this.children;
    /**
     * The Disclosure instance.
     * @type {Disclosure}
     */
    this.disclosure = new Disclosure({
      controller,
      target,
      onOpen: this.updateControllerText,
      onClose: this.updateControllerText,
    });

    // Add an aria-description about tabbing backward to the input
    const helpSpan = createScreenReaderText(
      'header-search-help',
      // eslint-disable-next-line max-len
      __('Use the Shift key with the Tab key to tab back to the search input.', 'sciencenews'),
    );
    document.body.appendChild(helpSpan);

    // Make sure the input is completely hidden;
    target.style.clip = 'rect(0, 0, 0, 0)';

    target.addEventListener('transitionend', () => {
      const { expanded } = this.disclosure.state;

      if (!expanded) {
        target.style.clip = 'rect(0, 0, 0, 0)';
      }
    });
  }

  /**
   * Update button text to reflect the disclosure's state.
   */
  updateControllerText() {
    const {
      controller,
      target,
      openText,
      closeText,
      input,
    } = this.children;
    const { expanded } = this.disclosure.state;

    // Show the open label when the disclosure is closed.
    openText.setAttribute('aria-hidden', `${expanded}`);
    // Show the close label when the disclosure is open.
    closeText.setAttribute('aria-hidden', `${!expanded}`);

    if (expanded) {
      target.style.clip = 'auto';
      // Moving to the search field is unintuitive due to the markup being out-of-order
      // Add the help text when the disclosure is open
      controller.setAttribute('aria-describedby', 'header-search-help');
      input.focus();
    } else {
      controller.removeAttribute('aria-describedby');
    }
  }
}
